import { useCallback, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { getRouteApi, useRouter } from '@tanstack/react-router'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

const routeApi = getRouteApi('/_app/invoices/$id')

type SubmitInvoiceFileRequest = {
  file: File
  invoiceSubmittalId: string
}

const uploadInvoiceFile = (item: SubmitInvoiceFileRequest) => {
  const form = new FormData()
  form.append('file', item.file)
  form.append('invoiceSubmittalId', item.invoiceSubmittalId)
  return Request.post(Request.urls.bank.invoiceSubmittalFileUpload, form)
}

export const useUpload = () => {
  const router = useRouter()
  const id = routeApi.useParams({ select: (s) => s.id })
  const [invoiceFile, setInvoiceFile] = useState<File | undefined>()

  const { mutate: saveInvoiceFile } = useMutation<void, Error, SubmitInvoiceFileRequest>({
    mutationFn: (opts) => uploadInvoiceFile(opts),
    onSuccess: async () => {
      Toast.successSmall(`Invoice File uploaded success.`)
      router.invalidate()
    },
    onError: (error) => handlerServerError(error)
  })

  const saveInvoiceFileHandler = useCallback(async () => {
    if (invoiceFile) {
      saveInvoiceFile({
        invoiceSubmittalId: String(id),
        file: invoiceFile
      })
    }
  }, [id, saveInvoiceFile, invoiceFile])

  const { Confirmation: InvoiceFileUploadConfirmation, open: openFilesUploadConfirmation } = useConfirmation({
    message: `Please confirm upload file: "${invoiceFile?.name}."`,
    onConfirm: saveInvoiceFileHandler,
    confirmationButtonText: 'Submit'
  })

  const fileInputChangeHandler = (value: File[]) => {
    if (!(value?.length > 0)) {
      return
    }
    setInvoiceFile(value[0])
    openFilesUploadConfirmation()
  }

  return { InvoiceFileUploadConfirmation, fileInputChangeHandler }
}
